import { useReducer, useRef, useCallback } from 'react';

export interface PlacedBunkerState {
  id: string;
  type: string;        // ArenaObjectType enum name (e.g. "BRICK")
  xFt: number;         // game X: -60 to +60 (field width axis)
  zFt: number;         // game Z: -75 to +75 (field length axis, 0 = centerline)
  rotationDeg: number; // Y-axis rotation in degrees
}

let _nextId = 1;
export function generateId(): string {
  return `b${_nextId++}`;
}

interface HistoryState {
  past: PlacedBunkerState[][];
  present: PlacedBunkerState[];
  future: PlacedBunkerState[][];
}

type HistoryAction =
  | { type: 'PUSH';    next: PlacedBunkerState[] }  // normal mutation — pushes to history
  | { type: 'REPLACE'; next: PlacedBunkerState[] }  // silent replace — no history entry
  | { type: 'UNDO' }
  | { type: 'REDO' };

const MAX_HISTORY = 100;

function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
  switch (action.type) {
    case 'PUSH':
      return {
        past:    [...state.past.slice(-(MAX_HISTORY - 1)), state.present],
        present: action.next,
        future:  [],
      };
    case 'REPLACE':
      return { ...state, present: action.next };
    case 'UNDO':
      if (state.past.length === 0) return state;
      return {
        past:    state.past.slice(0, -1),
        present: state.past[state.past.length - 1],
        future:  [state.present, ...state.future],
      };
    case 'REDO':
      if (state.future.length === 0) return state;
      return {
        past:    [...state.past, state.present],
        present: state.future[0],
        future:  state.future.slice(1),
      };
    default:
      return state;
  }
}

const INITIAL_STATE: HistoryState = { past: [], present: [], future: [] };

/** Hook that owns the placed-bunker array with full undo/redo history.
 *  setLayout() is the clean data boundary: a future auto-parser calls this
 *  to replace the whole layout without touching any React/Konva internals.
 *  replaceLayout() does the same but does NOT push a history entry — use it
 *  for internal transforms (e.g. orientation flips) that shouldn't be undoable. */
export function useLayoutState() {
  const [{ past, present, future }, dispatch] = useReducer(historyReducer, INITIAL_STATE);

  // Always-current ref so callbacks don't capture stale present values.
  const presentRef = useRef(present);
  presentRef.current = present;

  const placedBunkers = present;
  const canUndo = past.length > 0;
  const canRedo  = future.length > 0;

  const setLayout = useCallback((bunkers: PlacedBunkerState[]) => {
    dispatch({ type: 'PUSH', next: bunkers });
  }, []);

  const replaceLayout = useCallback((bunkers: PlacedBunkerState[]) => {
    dispatch({ type: 'REPLACE', next: bunkers });
  }, []);

  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);

  const addBunker = useCallback((bunker: Omit<PlacedBunkerState, 'id'>): string => {
    const id = generateId();
    dispatch({ type: 'PUSH', next: [...presentRef.current, { ...bunker, id }] });
    return id;
  }, []);

  const removeBunker = useCallback((id: string) => {
    dispatch({ type: 'PUSH', next: presentRef.current.filter(b => b.id !== id) });
  }, []);

  // Removes multiple bunkers in a single history entry (multi-select delete).
  const removeBunkers = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    dispatch({ type: 'PUSH', next: presentRef.current.filter(b => !idSet.has(b.id)) });
  }, []);

  const updateBunker = useCallback((id: string, updates: Partial<Omit<PlacedBunkerState, 'id'>>) => {
    dispatch({ type: 'PUSH', next: presentRef.current.map(b => b.id === id ? { ...b, ...updates } : b) });
  }, []);

  return {
    placedBunkers,
    setLayout, replaceLayout,
    addBunker, removeBunker, removeBunkers, updateBunker,
    undo, redo, canUndo, canRedo,
  };
}
